import { useTranslation } from "@sagot/i18n";
import { ErrorState, Skeleton, StatCard } from "@sagot/ui";
import { useFormatters } from "../shared/format";
import { goalRate, shareOf } from "./goal-stats";
import type { WorkflowGoalOut } from "./p4-types";
import { useWorkflowStats } from "./use-workflows";
import { windowLabel } from "./builder/goal-field";

const O = "engage.workflows.overview";
const DAYS = 30;

/** Sent / engaged / completed / goal / failed for the last 30 days, then how often each step was reached. */
export function OverviewPanel({ workflowId, goal }: { workflowId: string; goal: WorkflowGoalOut | null }) {
  const { t } = useTranslation();
  const format = useFormatters();
  const stats = useWorkflowStats(workflowId, DAYS, true);
  if (stats.isError) return <ErrorState title={t("engage.saveFailed")} onRetry={() => void stats.refetch()} retryLabel={t("common.retry")} />;
  if (stats.isPending) return <Skeleton className="h-48 rounded-card" />;
  const { counts, steps } = stats.data;
  const window = goal ? windowLabel(goal.within_hours) : null;
  return (
    <div className="space-y-6">
      <p className="text-sm font-semibold text-fg-muted">{t(`${O}.range`, { days: DAYS })}</p>
      <section className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <StatCard label={t(`${O}.sent`)} value={format.number(counts.sent)} icon="activity" footnote={t(`${O}.sentHint`)} />
        <StatCard label={t(`${O}.engaged`)} value={format.percent(shareOf(counts.engaged, counts.sent))} icon="message-circle" footnote={t(`${O}.engagedHint`, { formatted: format.number(counts.engaged) })} />
        <StatCard label={t(`${O}.completed`)} value={format.percent(shareOf(counts.completed, counts.sent))} icon="check-circle" footnote={t(`${O}.completedHint`)} />
        <StatCard
          label={t(`${O}.goal`)}
          value={format.percent(goalRate(counts))}
          icon="target"
          footnote={
            goal && window
              ? t(`${O}.goalHint`, { met: format.number(counts.goal_met), tracked: format.number(counts.goal_tracked), event: t(`engage.workflows.goal.events.${goal.event}`), window: t(`engage.workflows.goal.window_${window.unit}`, { count: window.count }) })
              : t(`${O}.goalNone`)
          }
        />
        <StatCard label={t(`${O}.failed`)} value={format.number(counts.failed)} icon="alert-triangle" footnote={t(`${O}.failedHint`)} />
      </section>
      {steps.length === 0 ? (
        <p className="text-sm text-fg-subtle">{t(`${O}.noData`)}</p>
      ) : (
        <section className="card overflow-x-auto" aria-label={t(`${O}.steps`)}>
          <table className="table">
            <thead>
              <tr>
                <th>{t(`${O}.steps`)}</th>
                <th className="text-right">{t(`${O}.reached`)}</th>
                <th className="text-right">{t(`${O}.failedSteps`)}</th>
              </tr>
            </thead>
            <tbody>
              {steps.map((step) => (
                <tr key={`${step.path}-${step.step_id}`}>
                  <td>
                    <span className="font-semibold">{t(`engage.workflows.steps.${step.type}`, { defaultValue: step.type })}</span>
                    <span className="ml-2 font-mono text-xs text-fg-subtle">{step.path} · {step.step_id}</span>
                  </td>
                  <td className="text-right tabular-nums">{step.reached}</td>
                  <td className={step.failed > 0 ? "text-right tabular-nums text-danger" : "text-right tabular-nums"}>{step.failed}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
